import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, CircleCheck, AlertCircle } from 'lucide-react';
import { cn } from '../../utils';

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const formatDateForDisplay = (value: string): string => {
  if (!value) return 'Selecciona una fecha';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 'Fecha inválida';
  return d.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
};

interface CalendarPickerProps {
  selectedDate: string;
  onChange: (date: string) => void;
  hourmeterDates?: Set<string>;
  maxDate?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({
  selectedDate,
  onChange,
  hourmeterDates = new Set(),
  maxDate,
  disabled = false,
  defaultOpen = false,
}) => {
  const selected = selectedDate ? new Date(`${selectedDate}T00:00:00`) : new Date();
  const [viewMonth, setViewMonth] = useState(selected.getMonth());
  const [viewYear, setViewYear] = useState(selected.getFullYear());
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (selectedDate) {
      const d = new Date(`${selectedDate}T00:00:00`);
      setViewMonth(d.getMonth());
      setViewYear(d.getFullYear());
    }
  }, [selectedDate]);

  const maxDateObj = maxDate ? new Date(`${maxDate}T00:00:00`) : null;

  const getDaysInMonth = (year: number, month: number) =>
    new Date(year, month + 1, 0).getDate();

  const getFirstDayOfMonth = (year: number, month: number) =>
    new Date(year, month, 1).getDay();

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const formatDateString = (year: number, month: number, day: number): string =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const isDateDisabled = (dateStr: string): boolean => {
    if (!maxDateObj) return false;
    const d = new Date(`${dateStr}T00:00:00`);
    return d > maxDateObj;
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isDuplicateDate = selectedDate && hourmeterDates.has(selectedDate);

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-gray-700">Fecha</label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!open) {
            const d = selectedDate ? new Date(`${selectedDate}T00:00:00`) : new Date();
            setViewMonth(d.getMonth());
            setViewYear(d.getFullYear());
          }
          setOpen((prev) => !prev);
        }}
        className={cn(
          'flex h-11 w-full items-center justify-between rounded-lg border bg-white px-4 text-sm shadow-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-retri-blue focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isDuplicateDate ? 'bg-[#fffbeb] border-gray-200' : 'border-gray-200',
          selectedDate ? 'text-gray-900 font-semibold' : 'text-gray-400',
        )}
      >
        <span>{formatDateForDisplay(selectedDate)}</span>
        <Calendar className="h-4 w-4 text-gray-400" />
      </button>

      {isDuplicateDate && (
        <div className="flex items-start gap-2.5 rounded-lg bg-[#fffbeb] px-4 py-3">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#d08700' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#d08700' }}>Advertencia: Fecha duplicada</p>
            <p className="text-sm text-gray-600">Ya existe un registro para esta fecha. Verifica antes de continuar.</p>
          </div>
        </div>
      )}

      {open && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={prevMonth}
              disabled={disabled}
              className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-sm font-semibold text-gray-900">
              {MONTHS_ES[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              disabled={disabled}
              className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {DAYS_ES.map((day) => (
              <div key={day} className="text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}

            {cells.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} />;
              }

              const dateStr = formatDateString(viewYear, viewMonth, day);
              const isSelected = dateStr === selectedDate;
              const isRegistered = hourmeterDates.has(dateStr);
              const dateDisabled = isDateDisabled(dateStr);

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => {
                    if (!dateDisabled && !disabled) {
                      onChange(dateStr);
                      setOpen(false);
                    }
                  }}
                  disabled={dateDisabled || disabled}
                  className={cn(
                    'relative mx-auto flex h-10 w-10 items-center justify-center rounded-lg text-sm transition-colors',
                    isSelected
                      ? 'bg-gray-900 text-white font-semibold'
                      : isRegistered
                        ? 'bg-[#dcfce7] text-[#0d542b] font-semibold'
                        : dateDisabled
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-900 hover:bg-gray-100',
                  )}
                >
                  {day}
                  {isRegistered && (
                    <CircleCheck className="absolute top-0 right-0 h-3 w-3 text-green-600" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
            <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-lg bg-[#dcfce7]">
              <CircleCheck className="absolute top-0 right-0 h-2.5 w-2.5 text-green-600" />
            </span>
            <span className="text-xs text-gray-500">Día ya registrado</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPicker;
